import {
  collection,
  onSnapshot,
  query,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { apiClient } from './apiClient';
import { Template, TemplateComponent } from '../../types/models/whatsapp';

const TEMPLATES = 'whatsapp_templates';

export interface TemplateDraft {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  components: TemplateComponent[];
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Templates live in Meta's system; we author them here, submit for approval,
 * and cache the result. Only APPROVED templates can be sent.
 */
class TemplateService {
  private map(id: string, data: any): Template {
    return {
      id,
      name: data.name || '',
      language: data.language || 'en',
      category: data.category || 'UTILITY',
      status: data.status || 'PENDING',
      qualityScore: data.qualityScore,
      components: Array.isArray(data.components) ? data.components : [],
      rejectionReason: data.rejectionReason,
      metaTemplateId: data.metaTemplateId,
      syncedAt: data.syncedAt || 0,
      createdAt: data.createdAt || 0
    };
  }

  /**
   * Live list. Templates change status asynchronously as Meta reviews them,
   * so the manager subscribes rather than polling.
   */
  subscribe(
    onChange: (templates: Template[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    // Deliberately unordered: Firestore silently DROPS documents that lack the
    // field you orderBy, so a template synced before createdAt was written
    // would be invisible rather than merely out of order. Sorting client-side
    // is safe at this collection's size and cannot hide a row.
    const q = query(collection(db, TEMPLATES));

    return onSnapshot(
      q,
      (snapshot) =>
        onChange(
          snapshot.docs
            .map((d) => this.map(d.id, d.data()))
            // Fall back to syncedAt so rows without createdAt still sort sanely.
            .sort(
              (a, b) =>
                (b.createdAt || b.syncedAt || 0) - (a.createdAt || a.syncedAt || 0)
            )
        ),
      (error) => {
        console.error('❌ Template subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  async list(): Promise<Template[]> {
    const response = await apiClient.get<{ templates: Template[] }>('/templates');
    return response.templates;
  }

  /** Approved templates only — what the composer's template picker shows. */
  async listSendable(): Promise<Template[]> {
    const templates = await this.list();
    return templates.filter((t) => t.status === 'APPROVED');
  }

  /**
   * Server-side validation without submitting.
   *
   * Meta rejects slowly and opaquely, and a rejected name is blocked for 30
   * days, so the builder checks before it commits.
   */
  async validate(draft: TemplateDraft): Promise<ValidationResult> {
    return apiClient.post<ValidationResult>('/templates/validate', draft);
  }

  /** Submits for approval. Returns PENDING; sync reconciles the outcome. */
  async create(draft: TemplateDraft): Promise<{ id: string; status: string }> {
    return apiClient.post<{ id: string; status: string }>('/templates', draft);
  }

  /** Deletes every language variant. Meta blocks the name for 30 days after. */
  async remove(name: string): Promise<{ success: boolean; note?: string }> {
    return apiClient.delete<{ success: boolean; note?: string }>(
      `/templates/${encodeURIComponent(name)}`
    );
  }

  async sync(): Promise<{ synced: number }> {
    return apiClient.post<{ synced: number }>('/templates/sync');
  }
}

export const templateService = new TemplateService();
export default templateService;
